import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ThemeBadge from '../components/ThemeBadge';
import NewsBadges from '../components/NewsBadges';
import NewPostModal from '../components/NewPostModal';
import RiskLight from '../components/RiskLight';
import { LEAD_STATUS_BADGE, LEAD_STATUS_LABEL, PLATFORM_LABEL, formatDateTime } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || '';

function LeadRow({ lead, onPromote, onDismiss }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-slate-800">
            {PLATFORM_LABEL[lead.platform] || lead.platform || 'Rede desconhecida'} · @{lead.handle_or_name}
          </p>
          <RiskLight rating={lead.rating} />
        </div>
        {lead.context && <p className="truncate text-xs text-slate-500">{lead.context}</p>}
        {lead.rating?.reason && <p className="mt-0.5 text-xs text-slate-500">{lead.rating.reason}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${LEAD_STATUS_BADGE[lead.status]}`}>
          {LEAD_STATUS_LABEL[lead.status] || lead.status}
        </span>
        {lead.status !== 'promovido' && lead.status !== 'descartado' && (
          <>
            <button
              onClick={() => onPromote(lead)}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
            >
              Promover a post
            </button>
            <button
              onClick={() => onDismiss(lead)}
              className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-400 hover:bg-slate-100"
            >
              Descartar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function NewsDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(null);
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    const { data } = await axios.get(`${API_URL}/api/news/${id}`);
    setArticle(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const dismiss = async (lead) => {
    await axios.patch(`${API_URL}/api/leads/${lead.id}`, { status: 'descartado' }, { headers });
    load();
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <p className="text-sm text-slate-400">Carregando…</p>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <p className="text-sm text-slate-400">Notícia não encontrada.</p>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-700">← Voltar</button>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <ThemeBadge value={article.theme} />
          <NewsBadges article={article} />
          <span className="text-xs text-slate-400">
            {article.source} · {article.locality || 'Localidade desconhecida'} · {formatDateTime(article.published_at || article.fetched_at)}
          </span>
        </div>
        <h1 className="text-xl font-bold text-slate-900">{article.title_pt || article.title}</h1>
        {article.title_pt && article.title_pt !== article.title && (
          <p className="mt-1 text-sm italic text-slate-400">Título original: {article.title}</p>
        )}
        {article.ai_summary && <p className="mt-2 text-sm text-slate-600">{article.ai_summary}</p>}
        {/* article.summary vem cru do RSS do Google News — na prática é só o título + a fonte
            (não um resumo editorial de verdade), então não exibimos para não duplicar o título. */}
        {article.classification_mock && (
          <p className="mt-2 text-xs italic text-amber-500">Classificação/tradução em modo mock (sem ANTHROPIC_API_KEY configurada)</p>
        )}
        <a
          href={article.url}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-block text-sm font-medium text-brand-600 hover:underline"
        >
          Ler matéria original ↗
        </a>
      </div>

      <div className="mt-6">
        <h2 className="mb-3 text-lg font-semibold text-slate-800">
          Perfis/posts citados nesta notícia ({article.leads?.length || 0})
        </h2>
        {!article.leads || article.leads.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-400">
            A IA não identificou menções a perfis específicos de redes sociais nesta notícia.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {article.leads.map((lead) => (
              <LeadRow key={lead.id} lead={lead} onPromote={setPromoting} onDismiss={dismiss} />
            ))}
          </div>
        )}
      </div>

      {promoting && (
        <NewPostModal
          endpoint={`/api/leads/${promoting.id}/promote`}
          submitLabel="Promover a post"
          initial={{ content: '', source_url: '' }}
          onClose={() => setPromoting(null)}
          onCreated={() => { setPromoting(null); load(); }}
        />
      )}
    </Layout>
  );
}

export default NewsDetail;
